import React, { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { Select, Form, Button } from '@agconnections/grow-ui';
import GrowUIFormField from 'components/Forms/GrowUIFormField';
import { Formik } from 'formik';
import { timingKeyValuePair } from 'screens/helpers/constants';
import useProductsData from 'hooks/useProductData';
import { Modal } from 'syngenta-digital-cropwise-react-ui-kit';
import ConvertPlanTable from '../ConvertPlanModal/ConvertPlanTable';
import './style.css';
import {
  ConvertPlanModalV2Props,
  PlanProductEntry,
  DropdownOption
} from './types';

const baseDropDownValues: DropdownOption[] = [
  { key: 'clear', value: '- Clear Filter' },
  { key: 'none', value: 'None' }
];

const initialValues = {
  timing: '',
  timingTag: ''
};

const ConvertPlanModalV2: React.FC<ConvertPlanModalV2Props> = ({
  open,
  currentPlan,
  title,
  description,
  conversionType,
  onSubmit,
  onClose,

  confirmButtonText = 'Next',
  cancelButtonText = 'Cancel'
}) => {
  const [mappedProducts, setMappedProducts] = useState<PlanProductEntry[]>([]);
  const [allAreMarked, setAllAreMarked] = useState(false);
  const { loading } = useProductsData();
  const [timings, setTimings] = useState<DropdownOption[]>(timingKeyValuePair);
  const [timingTags, setTimingTags] = useState<DropdownOption[]>([]);
  const [timingDropValue, setTimingDropValue] = useState<string | null>(null);
  const [timingTagDropValue, setTimingTagDropValue] = useState<string | null>(
    null
  );
  const [formSessionKey, setFormSessionKey] = useState(0);

  const resetMappedProducts = () => {
    const resetMapped = mappedProducts.map(prod => ({
      ...prod,
      hide: false,
      marked: false
    }));
    setMappedProducts(resetMapped);
  };

  const closeModal = () => {
    setTimingDropValue(null);
    setTimingTagDropValue(null);
    resetMappedProducts();
    setAllAreMarked(false);
    resetMappedProducts();
    onClose();
  };

  const handleSubmit = () => {
    const selectedProducts = mappedProducts.filter(p => p.marked);
    onSubmit(selectedProducts, conversionType);
  };

  const checkIfAllMarked = () => {
    const numberShown = mappedProducts?.filter(p => !p.hide).length;
    const numberMarked = mappedProducts?.filter(p => !p.hide && p.marked)
      .length;
    setAllAreMarked(numberShown === numberMarked && numberShown > 0);
  };

  const markUnmarkAll = (isMarked: boolean) => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prod.hide ? prod.marked : isMarked
    }));

    setMappedProducts(updatedProducts);
    checkIfAllMarked();
  };

  const markUnmarkProduct = (isMarked: boolean, prodIdx: string) => {
    const updatedProducts = mappedProducts.map(prod => ({
      ...prod,
      marked: prodIdx === prod.trackingId ? isMarked : prod.marked
    }));

    setMappedProducts(updatedProducts);
    checkIfAllMarked();
  };

  const filterTimingOnSelect = ({ key }: DropdownOption) => {
    const keyValue = key === 'none' ? '' : key;
    const updatedProducts = mappedProducts.map(prod => {
      const doesContain =
        keyValue === 'clear' ? prod.marked : keyValue === prod.timingEvent;
      return {
        ...prod,
        hide: key === 'clear' ? false : !doesContain
      };
    });

    setMappedProducts(updatedProducts);
    checkIfAllMarked();
  };

  const filterTimingTagsOnSelect = ({ key }: DropdownOption) => {
    const keyValue = key === 'none' ? '' : key;
    const updatedProducts = mappedProducts.map(prod => {
      const doesContain =
        keyValue === 'clear' ? prod.marked : keyValue === prod.timingEventTag;
      return {
        ...prod,
        hide: key === 'clear' ? false : !doesContain
      };
    });

    setMappedProducts(updatedProducts);
    checkIfAllMarked();
  };

  useEffect(() => {
    if (!currentPlan?.planProductEntries) {
      setMappedProducts([]);
      return;
    }

    const planProducts = currentPlan.planProductEntries.map(planProd => ({
      marked: false,
      hide: false,
      ...planProd
    }));
    setMappedProducts(planProducts);

    const newTimings = cloneDeep(baseDropDownValues);
    const combined = newTimings.concat(cloneDeep(timingKeyValuePair));
    setTimings(combined);

    const tempTimingTags = cloneDeep(baseDropDownValues);
    currentPlan.planProductEntries.forEach(prod => {
      if (
        prod.timingEventTag &&
        !tempTimingTags.some(tag => tag.key === prod.timingEventTag)
      ) {
        tempTimingTags.push({
          key: prod.timingEventTag,
          value: prod.timingEventTag
        });
      }
    });
    setTimingTags(tempTimingTags);
  }, [currentPlan?.planProductEntries]);

  // Set modal state to initial values when modal is opened
  useEffect(() => {
    if (open) {
      setTimingDropValue(null);
      setTimingTagDropValue(null);
      setFormSessionKey(k => k + 1);
    }
  }, [open]);

  useEffect(() => {
    checkIfAllMarked();
  });

  return (
    <Modal
      className="convert-plan-modal"
      open={open}
      onCancel={closeModal}
      closable={false}
      width="100%"
      footer={null}
    >
      <div className="flex flex-col convert-plan-modal-content">
        <div className="text-xl font-bold">{title}</div>
        <Formik
          key={formSessionKey}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ values, handleSubmit: formikHandleSubmit }) => {
            return (
              <>
                <div className="flex w-full w-1/3 mb-2">
                  <div className="w-full mr-4">
                    <Form.Field label="Timing">
                      <GrowUIFormField
                        control={Select}
                        value={timingDropValue}
                        items={timings}
                        id="timing"
                        name="timing"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const parsed = JSON.parse(e.target.value);
                          let dValue: string | null = null;
                          if (parsed.key !== 'clear') {
                            dValue = parsed.key;
                          }
                          filterTimingOnSelect(parsed);
                          setTimingDropValue(dValue);
                        }}
                      />
                    </Form.Field>
                  </div>
                  <Form.Field label="Timing Tag">
                    <GrowUIFormField
                      control={Select}
                      selected={values.timingTag}
                      value={timingTagDropValue}
                      items={timingTags}
                      id="timingTag"
                      name="timingTag"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const parsed = JSON.parse(e.target.value);
                        let dValue: string | null = null;
                        if (parsed.key !== 'clear') {
                          dValue = parsed.key;
                        }
                        filterTimingTagsOnSelect(parsed);
                        setTimingTagDropValue(dValue);
                      }}
                    />
                  </Form.Field>
                </div>
                <div className="text-xl font-bold">Products & Services</div>
                {description && (
                  <div className="text-xs mb-4">{description}</div>
                )}
                <div
                  className="mb-8 border border-solid overflow-y-auto w-full"
                  style={{ flex: 1 }}
                >
                  <ConvertPlanTable
                    planProducts={mappedProducts}
                    markAll={markUnmarkAll}
                    markUnmarkProduct={markUnmarkProduct}
                    loading={loading}
                    allAreMarked={allAreMarked}
                  />
                </div>
                <div className="flex justify-end">
                  <div className="mr-4">
                    <Button className="pr-2" onClick={closeModal}>
                      {cancelButtonText}
                    </Button>
                  </div>
                  <Button type="primary" onClick={formikHandleSubmit}>
                    {confirmButtonText}
                  </Button>
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
};

export default ConvertPlanModalV2;
